<template>
    <div class="card" v-show="loaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-4">
                    <h3 class="mb-0 text-uppercase">{{ $t('common.simulations') }}</h3>
                </div>
                <div class="col-8 text-right" :class="{'pr-0': canEditAll}">

                    <base-button type="secondary" class="mr-2" size="sm" @click="createAssessment" v-if="canEditAll">
                        <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                        <span class="btn-inner--text">{{ $t('assessments.new') }}</span>
                    </base-button>

                    <base-button type="primary" class="mr-0" size="sm" @click="createSimulation" v-if="canEditAll">
                        <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                        <span class="btn-inner--text">{{ $t('simulations.new') }}</span>
                    </base-button>

                    <actions-dropdown :actions="mainActions()" v-if="canEditAll"></actions-dropdown>
                </div>
            </div>
        </div>

        <p class="card-body pt-2 text-sm" v-if="empty">{{ $t('simulations.no-simulation') }}</p>

        <div class="pt-2" v-if="!empty">
            
            <el-table-draggable handle=".handle" @drop="dropped">
                <el-table class="table-responsive table-flush"
                        header-row-class-name="thead-light"
                        :data="ctrl.rows"
                        ref="simulations">

                    <el-table-column type="expand" class-name="expand">
                        <template v-slot="{row}">
                            <small class="description">{{ row.description }}</small>
                        </template>
                    </el-table-column>

                    <el-table-column :label="$t('common.simulation')" class-name="name">
                        <template v-slot="{row}">
                            <h4 class="mb-0">
                                <a href="#" @click.prevent="toggleSimulation(row)">
                                    {{ row.name }}
                                </a>
                                <small v-if="row.remedial">
                                    <i class="fas fa-undo ml-1"></i>
                                </small>
                                <i class="fas fa-clipboard-check ml-1" v-if="row.assessment"></i>
                                <i class="fas fa-lock ml-1" v-if="row.assessment && row.private"></i>
                            </h4>
                            <small v-if="row.edition">
                                {{ row.edition }}
                            </small>
                        </template>
                    </el-table-column>

                    <el-table-column :label="degree.simulation_role_1_name[degree.lang]">
                        <template v-slot="{row}">
                            {{ row.grid1 ? row.grid1.name : '' }}
                        </template>
                    </el-table-column>

                    <el-table-column :label="degree.simulation_role_2_name[degree.lang]" v-if="degree.simulation_role_2">
                        <template v-slot="{row}">
                            {{ row.grid2 ? row.grid2.name : '' }}
                        </template>
                    </el-table-column>

                    <el-table-column align="right" width="210" class-name="actions">
                        <template v-slot="{row}">
                            <div class="table-actions">

                                <a href="#" class="table-action text-danger" @click.prevent="ctrl.delete(row.id)" v-if="canEditAll">
                                    <i class="tim-icons icon-simple-remove"></i>
                                </a>

                                <a href="#" class="table-action text-primary" @click.prevent="ctrl.update(row.id)" v-if="canEdit">
                                    <i class="tim-icons icon-pencil"></i>
                                </a>

                                <a href="#" class="table-action text-info" @click.prevent="open(row)" v-if="!row.remedial && !row.assessment">
                                    <i class="tim-icons icon-bullet-list-67"></i>
                                </a>

                                <a href="#" class="table-action text-default handle" v-if="canEditAll">
                                    <i class="tim-icons icon-drag-31"></i>
                                </a>

                                <actions-dropdown :actions="simulationActions(row)"></actions-dropdown>

                            </div>
                        </template>
                    </el-table-column>
                </el-table>
            </el-table-draggable>
        </div>

        <portal to="modals">

            <file-importer ref="fileImporter"
                accept="application/json"
                :endpoint="logImportEndpoint"
                @uploaded="logImported"
                @error="logImportError"
            ></file-importer>

            <form-modal v-model="ctrl.modals.edit"
                :title="ctrl.form.data.assessment ? $t('assessments.assessment') : $t('common.simulation')"
                :loading="ctrl.form.loading"
            >
                <simulation-form v-model="ctrl.form.data"
                    :errors="ctrl.form.errors"
                    :modal="ctrl.modals.edit"
                    :degree="degree"
                    :duplicating="ctrl.form.duplicating"
                    :grids="grids"
                >
                </simulation-form>
            </form-modal>

            <confirm-modal v-model="ctrl.modals.confirmDelete">
                <p>{{ $t('simulations.delete-confirm') }}</p>
            </confirm-modal>

            <error-modal v-model="ctrl.modals.importFailed">
                <p>{{ $t('simulations.log-import-failed') }}</p>
            </error-modal>

            <success-modal v-model="ctrl.modals.importSucceeded">
                <p>{{ $t('simulations.log-import-succeeded') }}</p>
            </success-modal>

        </portal>

    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import SimulationForm from './SimulationForm'
    import ElTableDraggable from 'element-ui-el-table-draggable'

    export default {

        components: {
            SimulationForm, ElTableDraggable
        },

        props: {
            degree: {
                type: Object,
                required: true
            },
        },

        data() {
            return {
                loaded: false,
                ctrl: new CrudController({
                    endpoint: '/astral/api/front/simulations',
                    mapData: this.mapData,
                    unmapData: this.unmapData,
                    modals: ['importFailed', 'importSucceeded'],
                    query: {
                        filters: { degree_id: this.$route.params.id },
                        sort: ['position'],
                        relations: ['grid1', 'grid2'],
                        include: ['grids'],
                    }
                }),
                grids: {},
                simulationForImport: null,
            }
        },

        computed: {

            logImportEndpoint() {
                return this.simulationForImport
                    ? '/astral/api/front/simulations/' + this.simulationForImport.id + '/log/import'
                    : null
            },

            shared() {
                return this.degree.shared && this.$auth.user.owner_id && this.$auth.user.owner_id != this.degree.owner_id
            },

            canEdit() {
                return !this.shared && this.degree.status != 'archived'
            },
            
            canEditAll() {
                return !this.shared && this.degree.status != 'active' && this.degree.status != 'archived'
            },
            
            empty() {
                return this.ctrl.rows.length == 0
            }
        },

        created() {
            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
                this.grids = Array.isArray(resp.data.included.grids) ? {} : resp.data.included.grids
            })
        },

        methods: {

            createSimulation() {
                this.ctrl.create('edit')
                this.ctrl.form.data.assessment = false
            },

            createAssessment() {
                this.ctrl.create('edit')
                this.ctrl.form.data.assessment = true
            },

            mainActions() {
                return [
                    {
                        label: this.$t('common.paste'),
                        handler: this.pasteSimulation,
                    },
                ]
            },

            simulationActions(simulation) {
                let actions = []
                if (this.canEditAll) {
                    actions.push({
                        label: this.$t('common.duplicate'),
                        handler: this.duplicateSimulation,
                        data: simulation
                    })
                }
                actions.push({
                    label: this.$t('common.copy'),
                    handler: this.copySimulation,
                    data: simulation
                })
                actions.push({
                    label: this.$t('common.export-log'),
                    handler: this.exportLog,
                    data: simulation,
                    divided: 'true'
                })
                if (this.canEditAll) {
                    actions.push({
                        label: this.$t('common.import-log'),
                        handler: this.importLog,
                        data: simulation
                    })
                }
                return actions
            },

            duplicateSimulation(simulation) {
                this.ctrl.duplicate(simulation.id)
            },

            copySimulation(simulation) {
                axios.post(this.ctrl.client.endpoint + '/' + simulation.id + '/copy')
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })
            },

            pasteSimulation() {
                axios.post('/astral/api/front/degrees/' + this.degree.id + '/paste')
                .then(resp => {
                    this.ctrl.list()
                })
                .catch(err => {
                    if (err.response.status == 404) {
                        // Paste error. Do nothing.
                    } else {
                        this.$auth.handleErrorVm(err, this)
                    }
                })
            },

            toggleSimulation(simulation) {
                this.$refs.simulations.toggleRowExpansion(simulation)
            },

            importLog(simulation) {
                this.simulationForImport = simulation
                this.$refs.fileImporter.trigger()
            },

            logImported() {
                this.ctrl.modals.open('importSucceeded')
            },

            logImportError(resp) {
                this.ctrl.modals.open('importFailed')
            },

            exportLog(simulation) {
                window.open(this.ctrl.client.endpoint + '/' + simulation.id + '/log/export')
            },

            dropped(data) {
                let ids = data.list.map(item => item.id)
                axios.post('/astral/api/front/degrees/'+this.$route.params.id+'/reorder-simulations', { ids })
                .then(resp => {
                    this.ctrl.list()
                })
                .catch(err => {
                    //this.ctrl.list()
                    this.$auth.handleErrorVm(err, this)
                });
            },
            
            open(row) {
                this.$router.push({ name: 'admin-log', params: { degreeId: this.degree.id, simulationId: row.id } })
            },

            mapData(simulation) {
                return {
                    id: simulation ? simulation.id : null,
                    name: simulation ? simulation.name : null,
                    description: simulation ? simulation.description : null,
                    grid1_id: simulation ? simulation.grid1_id : null,
                    grid1: simulation ? simulation.grid1 : null,
                    grid2_id: simulation ? simulation.grid2_id : null,
                    grid2: simulation ? simulation.grid2 : null,
                    remedial: simulation ? simulation.remedial : false,
                    assessment: simulation ? simulation.assessment : false,
                    private: simulation ? simulation.private : false,
                    edition: simulation ? simulation.edition : null,
                }
            },

            unmapData(data) {
                data.degree_id = this.$route.params.id
                if (data.remedial) {
                    data.edition = null
                }
                return data
            }
        }
    }
</script>

<style scoped>

    .card-header >>> .el-dropdown i {
        top: 0 !important;
    }
    .description {
        white-space: pre-wrap;
    }

    .card >>> th,
    .card >>> td {
        padding-right: 0;
    }

    .card >>> .el-table td.expand {
        padding: 1rem;
    }

    .card >>> th.name,
    .card >>> td.name {
        padding-left: 0;
    }

    .card >>> th.actions,
    .card >>> td.actions {
        padding-right: 0.5rem;
    }
    
</style>